import FormItem from '../ui/FormItem';
import Button from '../ui/Buttons/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { apiCustomerGetCodeWithPhone } from '../../services/AuthService';
import { Link } from 'react-router-dom';

const validationSchema = Yup.object().shape({
  countryCode: Yup.string()
    .matches(/^\+\d{1,4}$/, 'Invalid code')
    .required('Required'),
  phone: Yup.string()
    .min(7, 'Please enter valid phone number.')
    .required('Please enter your phone number.'),
});

const LoginForm = ({ onGetCode }) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');


  const handleLogin = async (values) => {
    setIsFormSubmitting(true);
    setErrorMessage('');
    // Strip everything but digits from the user-typed phone and drop any
    // non-digit characters from the country code (keep the leading +) so
    // whatever we send to the backend is always strict E.164 — no parens,
    // dashes or spaces leaking through to the stored record.
    const rawCode = String(values.countryCode || '').replace(/[^\d]/g, '');
    const digits = String(values.phone || '').replace(/\D/g, '');
    const fullPhone = `+${rawCode}${digits}`;
    let payload = {
      phone: fullPhone,
    };

    try {
      const success = await apiCustomerGetCodeWithPhone(payload);
      if (success) {
        onGetCode(fullPhone);
        setIsFormSubmitting(false);
      } else {
        setErrorMessage('Failed to send verification code. Please try again.');
        setIsFormSubmitting(false);
      }
    } catch (error) {
      let message = 'An error occurred. Please try again.';
      if (error?.response?.data) {
        message =
          typeof error.response.data === 'string'
            ? error.response.data
            : error.response.data.message ||
              error.response.data.error ||
              message;
      }
      setErrorMessage(message);
      setIsFormSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <Formik
        initialValues={{
          countryCode: '+1',
          phone: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleLogin(values);
        }}
      >
        {({ touched, errors, isValid, dirty }) => {
          return (
            <Form>
              <FormItem
                label="Phone Number"
                invalid={(errors.phone && touched.phone) || (errors.countryCode && touched.countryCode)}
                errorMessage={errors.countryCode && touched.countryCode ? errors.countryCode : errors.phone}
              >
                <div className="flex items-center gap-2">
                  <Field
                    name="countryCode"
                    className="w-16 h-11 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors text-center"
                    placeholder="+1"
                    onFocus={() => errorMessage && setErrorMessage('')}
                  />
                  <Field
                    name="phone"
                    className="flex-1 h-11 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                    placeholder="Enter phone number"
                    onFocus={() => errorMessage && setErrorMessage('')}
                  />
                </div>
              </FormItem>
              {errorMessage && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 text-center">
                    {errorMessage}
                  </p>
                </div>
              )}
              <FormItem className="mt-5 !mb-0">
                <Button
                  loading={isFormSubmitting}
                  className="w-full px-4 !h-11"
                  variant="secondary"
                  type="submit"
                  disabled={!isValid || !dirty}
                >
                  Continue
                </Button>
                <p className="text-[11.5px] font-normal opacity-60 mt-3 text-darkblue leading-relaxed">
                  By clicking continue you agree to our{' '}
                  <Link to={'/terms'} target="_blank" className="underline">
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link to={'/privacy'} target="_blank" className="underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </FormItem>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default LoginForm;
